import moment from "moment";
import { produce } from "immer";
import {
  updateIncomingSchItems,
  addDependencies,
  updateSchItems,
  updateLfItems,
  setLockedFields,
  calculateTotals,
  calculateTotalCosts,
  calculateTotalInvestment,
  checkSectionsCompleted,
  setLargeFormatCreateModalData,
  updateQuarterlyServicesOnServiceChange,
  initialSheetState as initialState,
} from "features/Sheets/ManageSheet/ReducerHelpers";
import {
  getWizardSplitTotals,
  getLFPItems,
} from "features/Sheets/ManageSheet/helpers";
import { calculateComparisonSheetData } from "features/Sheets/ManageSheet/ComparisonSheet/Utils";
import { UtilityMeterDisplayCode } from "../../constants";

export const updateSheetData = (state, action) => {
  const sd = produce(action.applyChanges(state.sheetData), () => {});

  return produce({ ...state, sheetData: sd }, (draftState) => {
    const getCalculationData = (sheetsState) => sheetsState.rawCalculation;
    let newSheetData = draftState.sheetData;
    newSheetData.lastUpdated = Date.now(); // used to help detect changes to data, and cause re-renders
    newSheetData.isDirty = true;
    draftState.scheduleContent =
      newSheetData.systemScheduleItems.length +
      getLFPItems(newSheetData.largeFormatGroups).length;

    let { sheetData } = action;
    switch (action.field) {
      case "incomingSchItem":
        if (sheetData && sheetData.field == "unit") {
          updateIncomingSchItems(newSheetData, action.unitData);
        }
        break;
      case "systemScheduleItems":
        addDependencies(newSheetData, action.unitData, state.sheetData);
        updateSchItems(newSheetData, action.unitData);
        break;
      case "largeFormatGroups":
        updateLfItems(
          newSheetData,
          state.sheetData,
          action.largeFormatUnitData,
        );
        break;
      case "services":
        updateQuarterlyServicesOnServiceChange(
          state.sheetData.services,
          newSheetData.services,
          action.serviceData,
        );
        break;
      default:
        break;
    }

    const systemScheduleItemsCount = newSheetData.systemScheduleItems.filter(
      (x) => x.addedAsUtility,
    ).length;
    const lfpItemsCount = getLFPItems(newSheetData.largeFormatGroups).length;
    draftState.utilityMeters = systemScheduleItemsCount + lfpItemsCount;
    draftState.largeFormatGroupsItems = getLFPItems(
      newSheetData.largeFormatGroups,
    );

    // If applicable from is being updated and an expiry date is set, ensure expiry is not before the applicable from date
    if (
      sheetData &&
      sheetData.applicableFromDate &&
      newSheetData.rates.applicableFromDate &&
      newSheetData.rates.expiryDate &&
      moment(newSheetData.rates.expiryDate).isBefore(
        moment(newSheetData.rates.applicableFromDate),
      )
    ) {
      newSheetData.rates.expiryDate = newSheetData.rates.applicableFromDate;
    }

    // If locked rates fields in IP Syndicate rates are set, update Rates section
    setLockedFields(newSheetData.ipsRates, newSheetData.rates);

    const {
      funder,
      cofunded,
      dealer,
      funderSelected,
      cofundedSelected,
      dealerFunderSelected,
    } = newSheetData.capital;

    const funderTotals = funderSelected.value
      ? calculateTotals(funder)
      : getWizardSplitTotals();

    const coFundedTotals = cofundedSelected.value
      ? calculateTotals(cofunded)
      : getWizardSplitTotals();

    const dealerTotals = dealerFunderSelected.value
      ? calculateTotals(dealer)
      : getWizardSplitTotals();

    const funderTotalCosts = newSheetData.capital.funderSelected
      ? calculateTotalCosts(funder)
      : 0;

    const dealerTotalCosts = newSheetData.capital.dealerFunderSelected
      ? calculateTotalCosts(dealer)
      : 0;

    const coFundedTotalCosts = newSheetData.capital.cofundedSelected
      ? calculateTotalCosts(cofunded)
      : 0;

    const funderTotalInvestment = newSheetData.capital.funderSelected
      ? calculateTotalInvestment(newSheetData.capital.funder)
      : 0;

    const dealerTotalInvestment = newSheetData.capital.dealerFunderSelected
      ? calculateTotalInvestment(newSheetData.capital.dealer)
      : 0;

    const coFundedTotalInvestment = newSheetData.capital.cofundedSelected
      ? calculateTotalInvestment(newSheetData.capital.cofunded)
      : 0;

    const { userType, userArea } = action;

    const sectionsCompleted = checkSectionsCompleted(newSheetData);

    setLargeFormatCreateModalData(state.sheetData, newSheetData);

    newSheetData.capital.cofundedTotal = coFundedTotals.getTotal(
      userType,
      userArea,
    );
    newSheetData.capital.funderTotal = funderTotals.getTotal(
      userType,
      userArea,
    );
    newSheetData.capital.dealerTotal = dealerTotals.getTotal(
      userType,
      userArea,
    );

    newSheetData.capital.funderTotalCosts = funderTotalCosts;
    newSheetData.capital.dealerTotalCosts = dealerTotalCosts;
    newSheetData.capital.cofundedTotalCosts = coFundedTotalCosts;

    newSheetData.capital.funderTotalInvestment = funderTotalInvestment;
    newSheetData.capital.dealerTotalInvestment = dealerTotalInvestment;
    newSheetData.capital.coFundedTotalInvestment = coFundedTotalInvestment;

    newSheetData.comparisonData =
      calculateComparisonSheetData(newSheetData, getCalculationData(state)) ||
      initialState.comparisonData;

    draftState.capitalContent = getWizardSplitTotals(
      funderTotals.salesperson +
        dealerTotals.salesperson +
        coFundedTotals.salesperson,
      funderTotals.dealerAdmin +
        dealerTotals.dealerAdmin +
        coFundedTotals.dealerAdmin,
      funderTotals.ipsAdmin + dealerTotals.ipsAdmin + coFundedTotals.ipsAdmin,
    );

    draftState.ratesCompleted = sectionsCompleted.rates;
    draftState.capitalCompleted = sectionsCompleted.capital;
    draftState.servicesCompleted = sectionsCompleted.services;
    draftState.scheduleCompleted = sectionsCompleted.schedule;
    draftState.scheduleStabilityCompleted = sectionsCompleted.scheduleStability;
    draftState.paperCompleted = sectionsCompleted.paper;
    draftState.paperHasContent = sectionsCompleted.paperHasContent;
    draftState.largeFormatCompleted = sectionsCompleted.largeFormat;
  });
};
